/* eslint-disable react/jsx-pascal-case */

import "./App.css";
import "./Style.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import SEO from "./SEO/Seo";
import ScrollToSection from "./component/ScrollSec/ScrollToSec";
import RoleProtectedRoute from "./component/Dashboard/RoleProtectedRoute";
import { CoursesProvider } from "./component/Context/CoursesContext";

const Home = lazy(() => import("./component/Home"));
const Payment = lazy(() => import("./component/Payment"));
const UserDetails = lazy(() => import("./component/UserDetails"));
const Footer = lazy(() => import("./component/Footer").then((module) => ({ default: module.Footer })));
const LearnMore = lazy(() => import("./component/Learnmore"));
const AiMlCourse = lazy(() => import("./component/CourseDetails/Course/AiMlCourse"));
const AWSCourse = lazy(() => import("./component/CourseDetails/Course/AWSCourse"));
const MainFrameCourse = lazy(() => import("./component/CourseDetails/Course/MainFrameCourse"));
const JavaCourse = lazy(() => import("./component/CourseDetails/Course/JavaCourse"));
const DatabaseCourse = lazy(() => import("./component/CourseDetails/Course/DatabaseCourse"));
const PowerBICourse = lazy(() => import("./component/CourseDetails/Course/PowerBiCourse"));
const LinuxCourse = lazy(() => import("./component/CourseDetails/Course/LinuxCourse"));
const SoftskillCourse = lazy(() => import("./component/CourseDetails/Course/SoftSkilCourse"));
const CloudCourse = lazy(() => import("./component/CourseDetails/Course/CloudCourse"));
const PythonCourse = lazy(() => import("./component/CourseDetails/Course/PythonCourse"));
const AWS_Cloud_Course = lazy(() => import("./component/CourseDetails/Course/AWS_CloudCourse"));
const AWS_Data_Er_Course = lazy(() => import("./component/CourseDetails/Course/AWS_Data_Er_Course"));
const PrivacyPolicy = lazy(() => import("./Pages/PrivacYPolicy"));
const ContactUs = lazy(() => import("./Pages/ContactUs"));
const TermsAndConditions = lazy(() => import("./Pages/termsandCondition"));
const ShippingPolicy = lazy(() => import("./Pages/shippingpolocy"));
const FAQPage = lazy(() => import("./Pages/Faq"));
const AboutUS = lazy(() => import("./Pages/AboutUS."));
const UserDashboard = lazy(() => import("./Pages/UserDashboard"));
const AdminLayout = lazy(() => import("./component/Dashboard/AdminLayout"));
const AdminDashboard = lazy(() => import("./component/Dashboard/AdminDashboard"));
const AcademicCourses = lazy(() => import("./Pages/admin/AcademicCourses"));
const AcademicDoubts = lazy(() => import("./Pages/admin/AcademicDoubts"));
const TeacherLayout = lazy(() => import("./component/Dashboard/TeacherLayout"));
const TeacherDashboard = lazy(() => import("./component/Dashboard/TeacherDashboard"));
const TestPage = lazy(() => import("./component/UserDashboard/McqTest"));
const TestResultsPage = lazy(() => import("./component/UserDashboard/Testresult"));
const FreeVideos = lazy(() => import("./component/FreeVideo"));
const Python_Internship_Course = lazy(() => import("./component/CourseDetails/Course/Python_Internship_Course"));
const PythonInternshipRegistration = lazy(() => import("./component/CourseDetails/Brochure/PythonInternshipRegistration"));
const CloudComputingInternshipRegistration = lazy(() => import("./component/CourseDetails/Brochure/CloudComputingInternshipRegistration"));
const CppInternshipRegistration = lazy(() => import("./component/CourseDetails/Brochure/CppInternshipRegistration"));
const AIMLInternshipRegistration = lazy(() => import("./component/CourseDetails/Brochure/AIMLInternshipRegistration"));
const CPP_Internship_Course = lazy(() => import("./component/CourseDetails/Course/CPP_Internship_Course"));
const CloudComputing_Internship_Course = lazy(() => import("./component/CourseDetails/Course/CloudComputing_Internship_Course"));
const AIML_Internship_Course = lazy(() => import("./component/CourseDetails/Course/AIML_Internship_Course"));
const Academic = lazy(() => import("./Pages/Academic"));
const AcademicDashboard = lazy(() => import("./Pages/AcademicDashboard"));
const AcademicCourseViewer = lazy(() => import("./Pages/AcademicCourseViewer"));
const CourseData = lazy(() => import("./component/CourseDetails/CourseData"));

// ScrollToTop function to reset scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname !== "/") {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <CoursesProvider>
        <SEO />
        <Router>
          <ScrollToTop /> {/* Ensure ScrollToTop is inside Router */}
          <ScrollToSection />
          <Suspense fallback={<div className="portal-route-loading" role="status">Loading page…</div>}>
          <Routes>
            <Route>
              <Route path="/" element={<Home />} />
              <Route path="/userDetails" element={<UserDetails />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="/testresult" element={<TestResultsPage />} />
              <Route>
                <Route element={<RoleProtectedRoute allowedRoles={["2"]} />}>
                  <Route
                    path="/userDashboard"
                    element={<UserDashboard />}
                  ></Route>
                </Route>
                <Route element={<RoleProtectedRoute allowedRoles={["2"]} />}>
                  <Route
                    path="/academic-dashboard"
                    element={<AcademicDashboard />}
                  ></Route>
                </Route>
                <Route element={<RoleProtectedRoute allowedRoles={["2", "1"]} />}>
                  <Route
                    path="/academic-course/:courseId"
                    element={<AcademicCourseViewer />}
                  ></Route>
                </Route>
                <Route element={<RoleProtectedRoute allowedRoles={["2"]} />}>
                  <Route path="/test" element={<TestPage />}></Route>
                </Route>

                <Route element={<RoleProtectedRoute allowedRoles={["1"]} />}>
                  <Route path="/admin/*" element={<AdminLayout />}>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="academic-courses" element={<AcademicCourses />} />
                    <Route path="academic-doubts" element={<AcademicDoubts />} />
                  </Route>
                </Route>

                {/* Seller-only route */}
                <Route element={<RoleProtectedRoute allowedRoles={["3"]} />}>
                  <Route path="/teacher/*" element={<TeacherLayout />}>
                    <Route path="dashboard" element={<TeacherDashboard />} />
                  </Route>
                </Route>
              </Route>

              <Route path="/contact-us" element={<Footer />} />
              {/* <Route path="/register" element={<Registration />} /> */}
              <Route path="/learnmore" element={<LearnMore />} />
              <Route path="/AiMLCourse" element={<AiMlCourse />} />
              <Route path="/AWSCourse" element={<AWSCourse />} />
              <Route path="/MainFrameCourse" element={<MainFrameCourse />} />
              <Route path="/JavaCourse" element={<JavaCourse />} />
              <Route path="/DatabaseCourse" element={<DatabaseCourse />} />
              <Route path="/PowerBICourse" element={<PowerBICourse />} />
              <Route path="/LinuxCourse" element={<LinuxCourse />} />
              <Route path="/SoftSkillCourse" element={<SoftskillCourse />} />
              <Route path="/Cloud_Course" element={<CloudCourse />} />
              <Route path="/Python_Course" element={<PythonCourse />} />
              <Route path="/AWS_Cloud_Course" element={<AWS_Cloud_Course />} />
              <Route
                path="/PythonInternshipCourse"
                element={<Python_Internship_Course />}
              />
              <Route
                path="/PythonInternshipRegistration"
                element={<PythonInternshipRegistration />}
              />
              <Route
                path="/CloudComputingInternshipRegistration"
                element={<CloudComputingInternshipRegistration />}
              />
              <Route
                path="/CppInternshipRegistration"
                element={<CppInternshipRegistration />}
              />
              <Route
                path="/AIMLInternshipRegistration"
                element={<AIMLInternshipRegistration />}
              />
              <Route
                path="/CppInternshipCourse"
                element={<CPP_Internship_Course />}
              />
              <Route
                path="/CloudComputingInternshipCourse"
                element={<CloudComputing_Internship_Course />}
              />
              <Route
                path="/AIMLInternshipCourse"
                element={<AIML_Internship_Course />}
              />
              {/* <Route path="/free_videos" element={<FreeVideos />} /> */}
              <Route path="/free-videos/*" element={<FreeVideos />} />
              <Route
                path="/Aws_Data_Er_Course"
                element={<AWS_Data_Er_Course />}
              />
              <Route path="/privacy_policy" element={<PrivacyPolicy />} />
              <Route path="/ContactUs" element={<ContactUs />} />
              <Route path="/terms&Condition" element={<TermsAndConditions />} />
              <Route path="/shipping_policy" element={<ShippingPolicy />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/aboutUs" element={<AboutUS />} />
              <Route path="/academic/*" element={<Academic />} />
              <Route path="/courseData" element={<CourseData />} />
              {/* <Route path="/userDashboard" element={<UserDashboard />} /> */}
            </Route>
          </Routes>
          </Suspense>
        </Router>
      </CoursesProvider>
    </HelmetProvider>
  );
}

export default App;
